/**
 * The downloadable template that makes the import's file format self-evident.
 *
 * ⚠️ **This file is a second copy of a backend contract.** The columns below are
 * `KNOWN_COLUMNS` in `bulk_import/services/validation.py` and the row limit is its
 * `MAX_ROWS`, and this epic exists partly because a row cap that lived in two places
 * drifted between 100 and 500 across several years with no recorded reason on either
 * side. Nothing here is *enforced* client-side — every value and length is still
 * judged by the backend, and its rejection messages are what the admin is shown — but
 * a column renamed there and not here would hand out a template that is refused.
 *
 * The durable fix is a template served by the backend, which already derives its
 * failure report's columns from `KNOWN_COLUMNS` rather than restating them. Tracked as
 * a follow-up to KPS-600; until then, anything added to `KNOWN_COLUMNS` must be added
 * here in the same change.
 */

/** Columns the import reads, in the order it accepts them. */
export const TEMPLATE_COLUMNS = [
  'email',
  'first_name',
  'last_name',
  'title',
  'phone',
  'user_type',
  'locale',
  'timezone',
] as const

/** Columns every row must carry a value for. */
export const REQUIRED_COLUMNS: readonly string[] = [
  'email',
  'first_name',
  'last_name',
]

/**
 * Optional columns and what the import uses when a row leaves them blank, so an admin
 * can decide whether to fill them in before preparing a file rather than after.
 */
export const OPTIONAL_COLUMN_DEFAULTS: ReadonlyArray<
  readonly [column: string, defaultLabel: string]
> = [
  ['title', 'TEMPLATE_DEFAULT_EMPTY'],
  ['phone', 'TEMPLATE_DEFAULT_EMPTY'],
  ['user_type', 'TEAM_MEMBER'],
  ['locale', 'TEMPLATE_DEFAULT_ACCOUNT_LOCALE'],
  ['timezone', 'TEMPLATE_DEFAULT_UTC'],
]

/** Rows accepted in one import. */
export const MAX_ROWS = 500

export const TEMPLATE_FILENAME = 'bulk-user-import-template.csv'

/**
 * The template, as a header row and nothing else.
 *
 * Deliberately without an example row: the columns are what needs to be evident, and a
 * file carrying a plausible-looking person is a file somebody eventually uploads
 * unedited. What the optional columns default to, and the row limit, are stated on the
 * screen instead, where they can be read before the file is downloaded.
 */
export function buildTemplateCsv(): Blob {
  return new Blob([`${TEMPLATE_COLUMNS.join(',')}\n`], {
    type: 'text/csv;charset=utf-8',
  })
}
